// Graph-backed review & synthesis surface (step 5). Shows the AI's proposed
// clusters of same-meaning dialog extractions with their theme frequency
// ("N of M respondents"), and lets the PM curate: edit a title, reject, merge
// several into one, and promote a cluster into a native graph node (a
// job/challenge/opportunity tagged source:'synthesis').
// Generation streams from the Function URL; the proposal is read from the
// synthesis API. Propose-then-promote: only promoted clusters hit the graph.
// Also shows the already-promoted themes (read from the live graph) so the
// view is self-sufficient on return visits.

import { useCallback, useMemo, useState } from 'react';
import { Loader2, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { useCanvasData } from '@/hooks/use-canvas-data';
import type { NodeMetadata } from '@/services/canvas-mappers';
import { useSynthesis, useGenerateSynthesis } from '@/hooks/use-synthesis';
import { useUIStore } from '@/stores';
import { toast } from '@/hooks/use-toast';
import { pendingProposalFriction } from '@/lib/generation-friction';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { StepHeader } from '@/features/project/StepHeader';
import { PrerequisiteHint } from '@/features/project/PrerequisiteHint';
import { useMissingPrerequisite } from '@/features/project/use-missing-prerequisite';
import { runWasTruncated, type BandType } from '@/lib/synthesis-board';
import type { SynthesisAssignment, SynthesisCluster } from '@/types/api';
import { AffinityBoard, type AnchoredThemeRow } from './AffinityBoard';

// Stable empty props for the streaming call site: a fresh `[]` literal would
// change identity every render and defeat the board's memos.
const NO_CLUSTERS: SynthesisCluster[] = [];
const NO_ASSIGNMENTS: SynthesisAssignment[] = [];

interface SynthesisViewProps {
  projectId: string;
}

export function SynthesisView({ projectId }: SynthesisViewProps) {
  const { data: project } = useProject(projectId);
  const { data: proposal } = useSynthesis(projectId);
  // isSuccess, not just "canvas is truthy": this query is slower than the
  // synthesis proposal query (it fetches every node and edge), so there is a
  // real window where the proposal has loaded and canvas data has not, or has
  // failed. During that window canvas?.nodes is undefined, which must not be
  // treated as "an empty graph" — see AffinityBoard's canvasReady prop.
  const { data: canvas, isSuccess: canvasReady } = useCanvasData(projectId);
  const generate = useGenerateSynthesis();
  const updateProject = useUpdateProject();
  const canEdit = useUIStore((s) => s.canEdit);

  const reviewed = !!project?.synthesisReviewed;

  // A step id when the step has nothing to work with (no dialog extractions),
  // null when usable, undefined while the graph is still loading.
  const missing = useMissingPrerequisite(projectId, 'synthesis');
  const hinted = typeof missing === 'string';

  // Resolve member item ids → titles for a short preview.
  const labelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of canvas?.nodes ?? []) {
      m.set(n.id, (n.data as { label?: string } | undefined)?.label || 'Untitled');
    }
    return m;
  }, [canvas?.nodes]);

  const typeById = useMemo(() => {
    const m = new Map<string, BandType>();
    for (const n of canvas?.nodes ?? []) {
      if (n.type === 'job' || n.type === 'challenge' || n.type === 'opportunity') m.set(n.id, n.type);
    }
    return m;
  }, [canvas?.nodes]);

  const proposed = useMemo(
    () => (proposal?.clusters ?? []).filter((c) => c.status === 'proposed').sort((a, b) => b.respondentCount - a.respondentCount),
    [proposal?.clusters],
  );
  const respondentTotal = proposal?.respondentCountAtRun ?? 0;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const friction = pendingProposalFriction(proposed.length);

  // Already-promoted themes, read from the live graph rather than the proposal —
  // nodes promoted in earlier runs survive a re-synthesise that replaced the
  // proposal that produced them.
  const anchored = useMemo<AnchoredThemeRow[]>(
    () =>
      (canvas?.nodes ?? [])
        .filter((n) => (n.data?.metadata as NodeMetadata | undefined)?.source === 'synthesis')
        .filter((n) => n.type === 'job' || n.type === 'challenge' || n.type === 'opportunity')
        .map((n) => {
          const meta = n.data?.metadata as NodeMetadata | undefined;
          return {
            id: n.id,
            type: n.type as BandType,
            title: (n.data as { label?: string } | undefined)?.label || 'Untitled',
            respondentCount: meta?.respondentCount,
            respondentTotal: meta?.respondentTotal,
            memberIds: meta?.memberItemIds ?? [],
          };
        }),
    [canvas?.nodes],
  );

  const proposedAssignments = useMemo(
    () => (proposal?.assignments ?? []).filter((a) => a.status === 'proposed'),
    [proposal?.assignments],
  );

  const labelOf = useCallback((id: string) => labelById.get(id) ?? '(removed)', [labelById]);
  const typeOf = useCallback((id: string) => typeById.get(id), [typeById]);

  const { mutateAsync: generateSynthesis } = generate;
  const handleGenerate = useCallback(async () => {
    try {
      const clusters = await generateSynthesis(projectId);
      toast({ title: clusters.length ? `Found ${clusters.length} theme${clusters.length === 1 ? '' : 's'}` : 'No themes found' });
    } catch (err) {
      toast({ title: 'Synthesis failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  }, [generateSynthesis, projectId]);


  const setReviewed = async (value: boolean) => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { synthesisReviewed: value } });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  const generateDisabled = generate.isPending;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH} mx-auto space-y-6`}>
        <StepHeader
          projectId={projectId}
          stepId="synthesis"
          actions={
            <div className="flex items-center gap-2 shrink-0">
              {!hinted && (
                <Button
                  variant={reviewed ? 'outline' : 'brand'}
                  onClick={() => (friction === null ? handleGenerate() : setConfirmOpen(true))}
                  disabled={generateDisabled}
                >
                  {generate.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {proposal ? 'Re-synthesise' : 'Synthesise'}
                </Button>
              )}
              {reviewed ? (
                <Button variant="outline" onClick={() => setReviewed(false)} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : proposal ? (
                <Button variant="outline" onClick={() => setReviewed(true)} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {/* Keyed off every cluster the run produced, not the ones still awaiting
            review: the notice would otherwise vanish on the first promote. */}
        {proposal && runWasTruncated(proposal.clusters.length) && (
          <p className="text-muted-foreground text-xs">
            This run hit the limit of {proposal.clusters.length} themes, so some findings might not
            be grouped yet.
          </p>
        )}

        {generate.isPending ? (
          // Streamed previews land in their type band on the affinity board
          // while a run is in flight: read-only rows pop in as each cluster's
          // JSON completes. Already-promoted themes stay visible too — a new
          // run doesn't touch what's already committed to the graph.
          <div className="rounded-lg border bg-card p-4 space-y-3" aria-live="polite">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {generate.streamedClusters.length > 0
                ? `${generate.streamedClusters.length} theme${generate.streamedClusters.length === 1 ? '' : 's'} so far…`
                : generate.streamStarted
                ? 'Drafting the first theme…'
                : 'Synthesising your dialog findings…'}
            </div>
            <AffinityBoard
              projectId={projectId}
              clusters={NO_CLUSTERS}
              respondentTotal={respondentTotal}
              anchored={anchored}
              assignments={NO_ASSIGNMENTS}
              streamed={generate.streamedClusters}
              streaming
              labelOf={labelOf}
              typeOf={typeOf}
              canEdit={false}
              canvasReady={canvasReady}
            />
            {generate.draftingTitle && (
              <div className="flex items-center gap-2 rounded-md border border-dashed bg-card px-3 py-2 opacity-70 animate-pulse">
                <span className="min-w-0 flex-1 truncate text-sm">{generate.draftingTitle}</span>
                <span className="shrink-0 text-xs text-muted-foreground">drafting…</span>
              </div>
            )}
          </div>
        ) : !proposal && anchored.length === 0 ? (
          missing ? (
            // Nothing to synthesise yet: name the step that supplies dialog
            // extractions instead of offering a Run that would 400. Nested
            // inside the empty state so a proposal or promoted themes that
            // already exist keep rendering if the extractions are later deleted.
            <PrerequisiteHint stepId="synthesis" missing={missing} />
          ) : (
            // useSynthesis yields nothing while it loads, when the row is really
            // absent, and when the request fails. Only the middle case makes "no
            // synthesis yet" true, so a project with promoted themes falls through
            // to the board instead of being told its own graph is empty.
            <div className="rounded-lg border bg-card p-12 text-center">
              <Sparkles className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h2 className="font-semibold">No synthesis yet</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Run synthesis to group what respondents told you into themes. Needs at least two
                dialogs with submitted insights.
              </p>
            </div>
          )
        ) : proposed.length === 0 && anchored.length === 0 && proposedAssignments.length === 0 ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            No themes left to review. Re-synthesise to refresh.
          </div>
        ) : (
          // The board still renders here even when proposed.length === 0 —
          // anchored themes and unplaceable suggestions live inside it, and
          // hiding them when the proposal list empties was the regression
          // this fixes.
          <div className="space-y-5">
            {proposed.length === 0 && anchored.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Nothing new to review. Your promoted themes are below.
              </p>
            )}
            <AffinityBoard
              projectId={projectId}
              clusters={proposed}
              respondentTotal={respondentTotal}
              anchored={anchored}
              assignments={proposedAssignments}
              labelOf={labelOf}
              typeOf={typeOf}
              canEdit={canEdit}
              canvasReady={canvasReady}
            />
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Replace the proposals you haven't reviewed?"
        body={`You have ${friction ?? 0} proposed theme${friction === 1 ? '' : 's'} waiting for review. Re-synthesising replaces them.`}
        confirmLabel="Re-synthesise"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleGenerate();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
